import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Session Status React Components
 *
 * The expiry warning, and a developer panel showing this tab's local activity.
 *
 * The warning counts down to the deadline the **server** reports, not to a local
 * activity timestamp. It used to do the latter, which was coherent only while each
 * tab owned its own 30-minute clock: now that idle expiry lives on the session,
 * a locally-derived countdown is wrong in both directions at once — it warns about
 * a session another app has kept alive, and stays silent while a tab whose mouse is
 * moving quietly dies server-side (invariant I-10b).
 *
 * That is also why "Move your mouse to stay logged in" had to go. It was true when
 * a `mousemove` reset the deciding clock; it is false now, because the server's
 * clock only moves when a request carries an activity signal. The button does that
 * explicitly, and tells the truth about having done it.
 *
 * Plan: apps/auth/docs/session-idle-expiry-plan.md (Q3).
 *
 * @example
 * ```tsx
 * import { SessionStatus, SessionDebugPanel } from '@airclou/auth-react';
 *
 * function App() {
 *   return (
 *     <>
 *       <SessionStatus warningThresholdMinutes={2} />
 *       {import.meta.env.DEV && <SessionDebugPanel activityTracker={tracker} />}
 *     </>
 *   );
 * }
 * ```
 */
import { useCallback, useEffect, useState } from 'react';
import { useOptionalAppAuth } from '../AppAuthProvider';
import { LoadingSpinner } from '@airclou/ui';
export const SessionStatus = ({ warningThresholdMinutes = 2, devOnly = false, className, }) => {
    const auth = useOptionalAppAuth();
    // Only warn when the server will actually act. Through the rollout window a
    // deadline is reported but nothing is refused, so counting down to it would show a
    // countdown that reaches zero and then does nothing — and a warning that cries
    // wolf is worse than none, because it is how people learn to dismiss the real one.
    const idleExpiresAt = auth?.idleEnforced ? auth.idleExpiresAt : null;
    const [msRemaining, setMsRemaining] = useState(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isExtending, setIsExtending] = useState(false);
    const [extendFailed, setExtendFailed] = useState(false);
    // Manual sign-outs and idle sign-outs both fire `session:logout-start` so that
    // WebSocket and presence listeners can tear down. This popover is for the idle
    // case only: its job is to explain an unexpected redirect, and a manual sign-out
    // is self-explanatory.
    useEffect(() => {
        const handleLogoutStart = (event) => {
            const reason = event.detail?.reason;
            // Only the inactivity case. A manual sign-out is self-explanatory, and a
            // *revoked* one is not inactivity at all — an admin ended the session, or the
            // account was deactivated. Showing "you've been inactive for too long" there
            // is simply untrue, and it disagreed with the login URL, which correctly
            // carried no reason.
            if (reason !== 'inactivity')
                return;
            setIsLoggingOut(true);
        };
        window.addEventListener('session:logout-start', handleLogoutStart);
        return () => window.removeEventListener('session:logout-start', handleLogoutStart);
    }, []);
    useEffect(() => {
        if (devOnly && process.env.NODE_ENV === 'production')
            return;
        if (!idleExpiresAt) {
            setMsRemaining(null);
            return;
        }
        const deadline = Date.parse(idleExpiresAt);
        if (!Number.isFinite(deadline)) {
            setMsRemaining(null);
            return;
        }
        // A fresh deadline means the extension landed, so stop showing the failure.
        setExtendFailed(false);
        const tick = () => setMsRemaining(Math.max(0, deadline - Date.now()));
        tick();
        // One second is enough: the countdown is displayed in whole seconds, and the
        // old 100ms tick existed to dismiss the toast the instant the mouse moved —
        // which is no longer what dismisses it.
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [idleExpiresAt, devOnly]);
    const staySignedIn = useCallback(async () => {
        if (!auth)
            return;
        setIsExtending(true);
        setExtendFailed(false);
        try {
            const next = await auth.reportActivity();
            // A null means the server did not give us a later deadline. Leaving the
            // warning up is the honest outcome — claiming an extension that did not
            // happen takes away the operator's only retry affordance.
            if (!next)
                setExtendFailed(true);
        }
        finally {
            setIsExtending(false);
        }
    }, [auth]);
    if (devOnly && process.env.NODE_ENV === 'production')
        return null;
    // Includes zero, deliberately. Requiring `> 0` made the toast vanish the instant
    // the countdown ran out, and the "Signing out" state only appears once the next
    // tick gets the refusal — up to a minute later, longer in a throttled background
    // tab. The operator watched it hit zero, saw the warning disappear, and nothing
    // happened: indistinguishable from a false alarm, which is the cry-wolf outcome the
    // whole warning is meant to avoid.
    const showWarning = msRemaining !== null && msRemaining <= warningThresholdMinutes * 60 * 1000;
    if (!showWarning && !isLoggingOut)
        return null;
    const secondsLeft = Math.ceil((msRemaining ?? 0) / 1000);
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    const countdown = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    return (_jsx("div", { className: className || 'fixed bottom-4 right-4 z-50 max-w-sm', children: _jsx("div", { className: `${isLoggingOut
                ? 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-900'
                : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-900'} border rounded-lg shadow-lg p-4 flex items-start gap-3`, children: isLoggingOut ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex-shrink-0 mt-0.5", children: _jsx(LoadingSpinner, { size: "small", spinnerClassName: "border-red-600" }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "text-sm font-semibold text-red-900 dark:text-red-100", children: "Signing out" }), _jsx("p", { className: "text-sm text-red-800 dark:text-red-200 mt-1", children: "You've been inactive for too long. Redirecting to the login page." })] })] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { className: "w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5", fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "text-sm font-semibold text-yellow-900 dark:text-yellow-100", children: secondsLeft > 0 ? `Signing out in ${countdown}` : 'Signing out' }), _jsx("p", { className: "text-sm text-yellow-800 dark:text-yellow-200 mt-1", children: "Your session is about to expire because of inactivity. Save anything unfinished." }), extendFailed && (_jsx("p", { className: "text-xs text-red-700 dark:text-red-300 mt-2", children: "Couldn't reach the server. Try again." })), _jsx("button", { type: "button", onClick: staySignedIn, disabled: isExtending || !auth, className: "mt-3 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60", children: isExtending ? 'Staying signed in…' : 'Stay signed in' })] })] })) }) }));
};
/**
 * Developer-only session debug panel
 * Shows detailed session information in real-time
 *
 * This component only renders in development mode.
 */
export const SessionDebugPanel = ({ activityTracker, className }) => {
    const auth = useOptionalAppAuth();
    const [inactiveTime, setInactiveTime] = useState(0);
    useEffect(() => {
        // Only show in development
        if (process.env.NODE_ENV === 'production') {
            return;
        }
        // Only this tab's own input. Whether the *session* is idle is the server's
        // answer, shown beside it, and the two disagreeing is normal rather than a bug:
        // another app on the same session keeps the deadline moving while this tab sits
        // untouched.
        const update = () => setInactiveTime(activityTracker.getInactiveTime());
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [activityTracker]);
    // Don't render in production
    if (process.env.NODE_ENV === 'production') {
        return null;
    }
    const formatTime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    return (_jsxs("div", { className: className || "fixed bottom-4 left-4 z-50 bg-gray-900 text-white rounded-lg shadow-lg p-3 text-xs font-mono", children: [_jsxs("div", { className: "flex items-center gap-2 mb-2 text-gray-400", children: [_jsx("svg", { className: "w-3 h-3", fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { d: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" }) }), _jsx("span", { className: "font-semibold", children: "Session Debug" })] }), _jsxs("div", { className: "space-y-1", children: [_jsxs("div", { className: "flex justify-between gap-4", children: [_jsx("span", { className: "text-gray-400", children: "This tab idle:" }), _jsx("span", { className: inactiveTime > 60000 ? 'text-yellow-400' : 'text-green-400', children: formatTime(inactiveTime) })] }), _jsxs("div", { className: "flex justify-between gap-4", children: [_jsx("span", { className: "text-gray-400", children: "Session expires:" }), _jsx("span", { className: "text-green-400", children: auth?.idleExpiresAt
                                    ? formatTime(Math.max(0, Date.parse(auth.idleExpiresAt) - Date.now()))
                                    : '—' })] })] })] }));
};
